'use client';
import { useTranslations } from 'next-intl';
import { createContext, useContext, useState } from 'react';
import type { ChipValue } from '../../../Chip/Chip';
import type { ContentType } from '../../../types';
import type { ProductVerticalCategory } from '../../types/types';

type PromotionsFilterProviderProps = {
  children: React.ReactNode;
};

type PromotionsFilterContextType = {
  filters: ProductVerticalCategory[];
  selectedFilters: ProductVerticalCategory[];
  chips: ChipValue<ProductVerticalCategory>[];
  filteredOffers: Partial<ContentType>[];
  setSelectedFilters: (selectedFilters: ProductVerticalCategory[]) => void;
  setPromotions: (promotions: Partial<ContentType>[]) => void;
};

const PromotionsFilterContext = createContext<PromotionsFilterContextType | null>(null);

/** Verticals retired from UK product surface — exclude from filter chips even if API still tags offers. */
const EXCLUDED_FILTER_VERTICALS: ProductVerticalCategory[] = ['BINGO'];

function PromotionsFilterProvider({ children }: PromotionsFilterProviderProps) {
  const t = useTranslations('offers.filters');
  const [promotions, setPromotions] = useState<Partial<ContentType>[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<ProductVerticalCategory[]>([]);

  const filters = [...new Set(promotions.flatMap(item => item.verticals))]
    .filter((item): item is ProductVerticalCategory => item !== undefined && !EXCLUDED_FILTER_VERTICALS.includes(item))
    .sort((aa, bb) => aa.localeCompare(bb));
  const filteredOffers = selectedFilters.length > 0 ? promotions.filter(item => item.verticals?.some(vertical => selectedFilters.includes(vertical))) : promotions;
  const chips: ChipValue<ProductVerticalCategory>[] = filters.map(filter => ({
    label: t(`by_vertical.verticals.${filter}`),
    value: filter,
  }));

  const value = { filters, selectedFilters, setSelectedFilters, chips, filteredOffers, setPromotions };
  return <PromotionsFilterContext.Provider value={value}>{children}</PromotionsFilterContext.Provider>;
}

function usePromotionsFilter() {
  const context = useContext(PromotionsFilterContext);
  if (!context) {
    throw new Error('usePromotionsFilter must be used within a PromotionsFilterProvider');
  }
  return context;
}

export { PromotionsFilterProvider, usePromotionsFilter };
export type { PromotionsFilterContextType };
